import React, {
    useState,
    useEffect,
    useReducer,
    useCallback,
    useContext,
} from "react";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import { DeleteOutline, Edit, AttachFile, Add, Search } from "@material-ui/icons";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

import FileModal from "../../components/FileModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import ForbiddenPage from "../../components/ForbiddenPage";
import TableRowSkeleton from "../../components/TableRowSkeleton";

const reducer = (state, action) => {
    if (action.type === "LOAD_FILES") {
        const files = action.payload;
        const newFiles = [];

        files.forEach((fileList) => {
            const fileListIndex = state.findIndex((s) => s.id === fileList.id);
            if (fileListIndex !== -1) {
                state[fileListIndex] = fileList;
            } else {
                newFiles.push(fileList);
            }
        });

        return [...state, ...newFiles];
    }

    if (action.type === "UPDATE_FILES") {
        const fileList = action.payload;
        const fileListIndex = state.findIndex((s) => s.id === fileList.id);

        if (fileListIndex !== -1) {
            state[fileListIndex] = fileList;
            return [...state];
        } else {
            return [fileList, ...state];
        }
    }

    if (action.type === "DELETE_FILE") {
        const fileListId = action.payload;

        const fileListIndex = state.findIndex((s) => s.id === fileListId);
        if (fileListIndex !== -1) {
            state.splice(fileListIndex, 1);
        }
        return [...state];
    }

    if (action.type === "RESET") {
        return [];
    }
};

const useStyles = makeStyles((theme) => ({
    container: {
        padding: "24px",
        background: "#f5f7fa",
        minHeight: "100vh",
    },
    header: {
        marginBottom: "24px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
        gap: "16px",
    },
    title: {
        fontSize: "24px",
        fontWeight: 700,
        color: "#1a1a1a",
        margin: 0,
        display: "flex",
        alignItems: "center",
        gap: "12px",
    },
    headerActions: {
        display: "flex",
        gap: "12px",
        alignItems: "center",
        flexWrap: "wrap",
    },
    searchContainer: {
        position: "relative",
        display: "flex",
        alignItems: "center",
    },
    searchInput: {
        padding: "10px 12px 10px 40px",
        border: "1px solid #e8ecf1",
        borderRadius: "6px",
        fontSize: "13px",
        fontFamily: "inherit",
        background: "white",
        transition: "all 0.2s ease",
        minWidth: "250px",
        "&:focus": {
            outline: "none",
            borderColor: theme.palette.primary.main,
            boxShadow: `0 0 0 3px ${theme.palette.primary.main}20`,
        },
    },
    searchIcon: {
        position: "absolute",
        left: "12px",
        color: "#666",
        fontSize: "20px",
    },
    addButton: {
        padding: "10px 20px",
        background: theme.palette.primary.main,
        color: "white",
        border: "none",
        borderRadius: "6px",
        fontSize: "13px",
        fontWeight: 600,
        cursor: "pointer",
        transition: "all 0.3s ease",
        display: "flex",
        alignItems: "center",
        gap: "6px",
        "&:hover": {
            background: theme.palette.primary.dark,
            transform: "translateY(-1px)",
            boxShadow: "0 2px 8px rgba(59, 130, 246, 0.3)",
        },
    },
    tableCard: {
        background: "white",
        borderRadius: "8px",
        padding: "20px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        border: "1px solid #e8ecf1",
        overflow: "hidden",
    },
    tableContainer: {
        overflowX: "auto",
        maxHeight: "calc(100vh - 250px)",
        overflowY: "auto",
    },
    table: {
        width: "100%",
        borderCollapse: "collapse",
    },
    tableHeader: {
        background: "#f9fafb",
        borderBottom: "2px solid #e8ecf1",
        position: "sticky",
        top: 0,
        zIndex: 1,
    },
    tableHeaderCell: {
        padding: "12px",
        textAlign: "center",
        fontSize: "12px",
        fontWeight: 600,
        color: "#666",
        whiteSpace: "nowrap",
    },
    tableRow: {
        borderBottom: "1px solid #e8ecf1",
        transition: "background 0.2s ease",
        "&:hover": {
            background: "#f9fafb",
        },
    },
    tableCell: {
        padding: "12px",
        fontSize: "13px",
        color: "#1a1a1a",
        textAlign: "center",
    },
    actionButtons: {
        display: "flex",
        gap: "6px",
        justifyContent: "center",
    },
    actionButton: {
        background: "none",
        border: "none",
        cursor: "pointer",
        padding: "6px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "4px",
        transition: "all 0.2s ease",
        "&:hover": {
            background: "#f0f0f0",
        },
    },
    emptyState: {
        textAlign: "center",
        padding: "40px 20px",
        color: "#999",
    },
    loadMoreButton: {
        width: "100%",
        padding: "12px",
        marginTop: "16px",
        background: "#f9fafb",
        border: "1px solid #e8ecf1",
        borderRadius: "6px",
        fontSize: "13px",
        fontWeight: 600,
        color: "#666",
        cursor: "pointer",
        transition: "all 0.2s ease",
        "&:hover": {
            background: "#f0f0f0",
            borderColor: theme.palette.primary.main,
            color: theme.palette.primary.main,
        },
    },
}));

const FileLists = () => {
    const classes = useStyles();
    const { user, socket } = useContext(AuthContext);

    const [loading, setLoading] = useState(false);
    const [pageNumber, setPageNumber] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [selectedFileList, setSelectedFileList] = useState(null);
    const [deletingFileList, setDeletingFileList] = useState(null);
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [searchParam, setSearchParam] = useState("");
    const [files, dispatch] = useReducer(reducer, []);
    const [fileListModalOpen, setFileListModalOpen] = useState(false);

    const fetchFileLists = useCallback(async () => {
        try {
            const { data } = await api.get("/files/", {
                params: { searchParam, pageNumber },
            });
            dispatch({ type: "LOAD_FILES", payload: data.files });
            setHasMore(data.hasMore);
            setLoading(false);
        } catch (err) {
            toastError(err);
        }
    }, [searchParam, pageNumber]);

    useEffect(() => {
        dispatch({ type: "RESET" });
        setPageNumber(1);
    }, [searchParam]);

    useEffect(() => {
        setLoading(true);
        const delayDebounceFn = setTimeout(() => {
            fetchFileLists();
        }, searchParam ? 250 : 100); // Reduzir delay: 250ms para busca, 100ms para mudanças
        return () => clearTimeout(delayDebounceFn);
    }, [searchParam, pageNumber, fetchFileLists]);

    useEffect(() => {
        const onFileEvent = (data) => {
            if (data.action === "update" || data.action === "create") {
                dispatch({ type: "UPDATE_FILES", payload: data.files });
            }

            if (data.action === "delete") {
                dispatch({ type: "DELETE_FILE", payload: +data.fileId });
            }
        };

        socket.on(`company-${user.companyId}-file`, onFileEvent);
        return () => {
            socket.off(`company-${user.companyId}-file`, onFileEvent);
        };
    }, [socket, user.companyId]);

    const handleOpenFileListModal = () => {
        setSelectedFileList(null);
        setFileListModalOpen(true);
    };

    const handleCloseFileListModal = () => {
        setSelectedFileList(null);
        setFileListModalOpen(false);
    };

    const handleSearch = (event) => {
        setSearchParam(event.target.value);
    };

    const handleEditFileList = (fileList) => {
        setSelectedFileList(fileList);
        setFileListModalOpen(true);
    };

    const handleDeleteFileList = async (fileListId) => {
        try {
            await api.delete(`/files/${fileListId}`);
            toast.success(i18n.t("files.toasts.deleted"));
        } catch (err) {
            toastError(err);
        }
        setDeletingFileList(null);
        setSearchParam("");
        setPageNumber(1);

        dispatch({ type: "RESET" });
        setPageNumber(1);
        await fetchFileLists();
    };

    const loadMore = () => {
        setPageNumber((prevState) => prevState + 1);
    };

    if (user.profile === "user") {
        return <ForbiddenPage />;
    }

    return (
        <div className={classes.container}>
            <ConfirmationModal
                title={deletingFileList && `${i18n.t("files.confirmationModal.deleteTitle")}`}
                open={confirmModalOpen}
                onClose={setConfirmModalOpen}
                onConfirm={() => handleDeleteFileList(deletingFileList.id)}
            >
                {i18n.t("files.confirmationModal.deleteMessage")}
            </ConfirmationModal>

            <FileModal
                open={fileListModalOpen}
                onClose={handleCloseFileListModal}
                reload={fetchFileLists}
                aria-labelledby="form-dialog-title"
                fileListId={selectedFileList && selectedFileList.id}
            />

            {/* Header */}
            <div className={classes.header}>
                <h1 className={classes.title}>
                    <AttachFile style={{ fontSize: "28px" }} />
                    {i18n.t("files.title")} ({files.length})
                </h1>
                <div className={classes.headerActions}>
                    <div className={classes.searchContainer}>
                        <Search className={classes.searchIcon} />
                        <input
                            type="text"
                            placeholder={i18n.t("contacts.searchPlaceholder")}
                            value={searchParam}
                            onChange={handleSearch}
                            className={classes.searchInput}
                        />
                    </div>
                    <button className={classes.addButton} onClick={handleOpenFileListModal}>
                        <Add style={{ fontSize: "20px" }} />
                        {i18n.t("files.buttons.add")}
                    </button>
                </div>
            </div>

            {/* Tabela */}
            <div className={classes.tableCard}>
                {files.length === 0 && !loading ? (
                    <div className={classes.emptyState}>
                        <p>Nenhuma lista de arquivos encontrada</p>
                    </div>
                ) : (
                    <>
                        <div className={classes.tableContainer}>
                            <table className={classes.table}>
                                <thead className={classes.tableHeader}>
                                    <tr>
                                        <th className={classes.tableHeaderCell}>
                                            {i18n.t("files.table.name")}
                                        </th>
                                        <th className={classes.tableHeaderCell}>
                                            {i18n.t("files.table.actions")}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {files.map((fileList) => (
                                        <tr key={fileList.id} className={classes.tableRow}>
                                            <td className={classes.tableCell}>
                                                {fileList.name}
                                            </td>
                                            <td className={classes.tableCell}>
                                                <div className={classes.actionButtons}>
                                                    <button
                                                        className={classes.actionButton}
                                                        onClick={() => handleEditFileList(fileList)}
                                                        title="Editar"
                                                    >
                                                        <Edit style={{ fontSize: "18px", color: "#3b82f6" }} />
                                                    </button>
                                                    <button
                                                        className={classes.actionButton}
                                                        onClick={() => {
                                                            setConfirmModalOpen(true);
                                                            setDeletingFileList(fileList);
                                                        }}
                                                        title="Deletar"
                                                    >
                                                        <DeleteOutline style={{ fontSize: "18px", color: "#ef4444" }} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                    {loading && <TableRowSkeleton columns={2} />}
                                </tbody>
                            </table>
                        </div>
                        {hasMore && (
                            <button
                                className={classes.loadMoreButton}
                                onClick={loadMore}
                                disabled={loading}
                            >
                                {loading ? "Carregando..." : "Carregar mais"}
                            </button>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default FileLists;
